import { add, getMonth, isBefore } from 'date-fns';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isUndefined } from 'lodash';
import { FieldLabel } from 'components/Forms';
import { IconButton } from 'components/IconButton';
import { RadioCardInput } from 'components/Forms/inputs/RadioCardInput';
import { useBreakpoint } from 'utils/hooks/useBreakpoint';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { Loader } from 'components/Loader';
import { AvailableTimes } from 'domains/calendars/types';

type Props = {
  availableTimes: AvailableTimes;
  onChange: (dayIndex: number) => void;
  selectedDayIndex?: number;
  onInfiniteScroll?: () => void;
  isLoading?: boolean;
  isScrollLoading?: boolean;
  maxDay?: Date;
  labelKey?: string;
  disabledWhenNoAvailableTimes?: boolean;
};
const DESKTOP_MAX_DAYS = 7;
const MOBILE_MAX_DAYS = 3;

export const DayPicker: React.FC<Props> = ({
  availableTimes,
  onChange,
  selectedDayIndex,
  onInfiniteScroll,
  isLoading = false,
  isScrollLoading,
  maxDay,
  labelKey,
  disabledWhenNoAvailableTimes = true,
}) => {
  const { format } = useDisplayTimezone();
  const { t } = useTranslation();
  const isMobile = useBreakpoint({
    max: 'sm',
  });

  const [scrollIndex, setScrollIndex] = useState<number>(
    isMobile && selectedDayIndex && selectedDayIndex >= MOBILE_MAX_DAYS
      ? selectedDayIndex
      : selectedDayIndex && selectedDayIndex >= DESKTOP_MAX_DAYS
      ? selectedDayIndex
      : 0,
  );

  const amountOfDaysToShow = isMobile ? MOBILE_MAX_DAYS : DESKTOP_MAX_DAYS;

  const visibleDayRange = useMemo(
    () => [scrollIndex, scrollIndex + amountOfDaysToShow],
    [scrollIndex, amountOfDaysToShow],
  );
  const visibleDays = useMemo(
    () =>
      availableTimes
        .map((day, index) => ({
          ...day,
          originalIndex: index,
        }))
        .slice(...visibleDayRange),
    [availableTimes, visibleDayRange],
  );

  const dayHeaders = useMemo(
    () =>
      visibleDays.map(({ day }, index) => {
        if (index === 0 || getMonth(day) !== getMonth(visibleDays[index - 1].day)) {
          return format(day, 'MMM yyy');
        }
        return '';
      }),
    [visibleDays, format],
  );

  const dayOptions = useMemo(
    () =>
      visibleDays.map(({ day, originalIndex, ...rest }) => ({
        value: `${originalIndex}`,
        label: format(day, `EEE '\n' d`),
        originalIndex,
        ...rest,
      })),
    [visibleDays, format],
  );

  const onScrollForward = () => {
    if (scrollIndex + 1 + amountOfDaysToShow > availableTimes.length) {
      onInfiniteScroll?.();
    }
    setScrollIndex(currentIndex => currentIndex + 1);
  };

  const isPastLastItem = scrollIndex + amountOfDaysToShow > availableTimes.length;

  const canScrollForward = useMemo(() => {
    if (isScrollLoading && isPastLastItem) {
      return false;
    }

    /** Simple case, no infinite scroll just up until data ends */
    if (isUndefined(onInfiniteScroll)) {
      return availableTimes.length - scrollIndex > amountOfDaysToShow;
    }

    /** Check if the next day to show lands after the max date */
    if (maxDay) {
      const nextDate = add(availableTimes[0].day, { days: scrollIndex + amountOfDaysToShow });
      return isBefore(nextDate, maxDay);
    }

    return true;
  }, [onInfiniteScroll, isScrollLoading, isPastLastItem, scrollIndex, amountOfDaysToShow, maxDay, availableTimes]);

  return (
    <div className="mb-64">
      <FieldLabel className="mb-16">{t(labelKey ?? 'consultation.schedule.availableDays')}</FieldLabel>
      <div className="grid grid-cols-[min-content_1fr_min-content] gap-x-8">
        <span />
        <div className="grid grid-cols-7 sm-d:grid-cols-3 gap-8 whitespace-pre text-center mb-8">
          {dayHeaders.map((label, index) => (
            <span className="text-black-38% font-semibold text-sm" key={label + index}>
              {label}
            </span>
          ))}
        </div>
        <span />
        <IconButton
          name="AngleLeft"
          variant="ghost"
          title="back"
          disabled={scrollIndex === 0}
          onClick={() => setScrollIndex(currentIndex => currentIndex - 1)}
          className="self-center"
        />
        <div className="grid grid-cols-7 sm-d:grid-cols-3 gap-8 whitespace-pre text-center">
          {dayOptions.map(option => (
            <RadioCardInput
              key={option.value}
              name="select-day"
              disabled={
                (disabledWhenNoAvailableTimes && option.availableTimes.length === 0) ||
                (isLoading && option.originalIndex !== selectedDayIndex)
              }
              checked={option.originalIndex === selectedDayIndex}
              onChange={() => onChange(option.originalIndex)}
              id={option.value}
              option={option}
              labelClassName="font-semibold"
            />
          ))}

          {isScrollLoading && isPastLastItem && (
            // An empty div is used so that it takes the exact same size as the other elements in the grid
            <div className="relative">
              <Loader defaultStyles className="absolute" />
            </div>
          )}
        </div>
        <IconButton
          name="AngleRight"
          variant="ghost"
          title="forward"
          disabled={!canScrollForward}
          onClick={onScrollForward}
          className="self-center"
        />
      </div>
    </div>
  );
};
